import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ArrowLeft, User, Building2, Upload, X, Save, Crown, AlertCircle, CheckCircle, Palette } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Badge } from '@/components/ui/Badge';
import { useAuth } from '@/hooks/useAuth';
import { useCompanyBranding } from '@/hooks/useCompanyBranding';
import { companyBrandingService } from '@/services/company-branding-service';
import { supabase } from '@/utils/supabase';

export const ProfilePage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { companyName, companyLogoUrl, primaryColor, isProUser, canCustomizePDF, refresh: refreshBranding } = useCompanyBranding();

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [editedCompanyName, setEditedCompanyName] = useState('');
  const [editedPrimaryColor, setEditedPrimaryColor] = useState<string>('');
  const [savingColor, setSavingColor] = useState(false);
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [logoPreview, setLogoPreview] = useState<string | null>(null);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [deletingLogo, setDeletingLogo] = useState(false);

  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  useEffect(() => {
    loadProfile();
  }, [user?.id]);

  useEffect(() => {
    setEditedCompanyName(companyName);
    setLogoPreview(companyLogoUrl);
    setEditedPrimaryColor(primaryColor ?? '');
  }, [companyName, companyLogoUrl, primaryColor]);

  const loadProfile = async () => {
    if (!user?.id) return;

    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('profiles')
        .select('first_name, last_name')
        .eq('id', user.id)
        .single();

      if (error) {
        console.error('Error loading profile:', error);
        return;
      }

      if (data) {
        setFirstName(data.first_name || '');
        setLastName(data.last_name || '');
      }
    } catch (error) {
      console.error('Load profile error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSavePersonalInfo = async () => {
    if (!user?.id) return;

    try {
      setSaving(true);
      setMessage(null);

      const { error } = await supabase
        .from('profiles')
        .update({
          first_name: firstName.trim(),
          last_name: lastName.trim()
        })
        .eq('id', user.id);

      if (error) {
        throw error;
      }

      setMessage({ type: 'success', text: 'Informations personnelles mises à jour avec succès.' });
    } catch (error) {
      console.error('Save personal info error:', error);
      setMessage({ type: 'error', text: 'Erreur lors de la mise à jour des informations.' });
    } finally {
      setSaving(false);
    }
  };

  const handleSaveCompanyName = async () => {
    if (!user?.id || !canCustomizePDF) return;

    try {
      setSaving(true);
      setMessage(null);

      console.log('[ProfilePage] Saving company name:', editedCompanyName);
      const result = await companyBrandingService.updateCompanyName(editedCompanyName, user.id);

      if (!result.success) {
        console.error('[ProfilePage] Save failed:', result.error);
        setMessage({ type: 'error', text: result.error || 'Erreur lors de la mise à jour.' });
        return;
      }

      console.log('[ProfilePage] Save successful, waiting before refresh...');
      await new Promise(resolve => setTimeout(resolve, 500));

      console.log('[ProfilePage] Refreshing branding data...');
      await refreshBranding();

      console.log('[ProfilePage] Branding refreshed successfully');
      setMessage({ type: 'success', text: 'Nom d\'entreprise mis à jour avec succès.' });
    } catch (error) {
      console.error('[ProfilePage] Save company name error:', error);
      setMessage({ type: 'error', text: 'Erreur lors de la mise à jour du nom d\'entreprise.' });
    } finally {
      setSaving(false);
    }
  };

  const handleSavePrimaryColor = async () => {
    if (!user?.id || !canCustomizePDF) return;

    try {
      setSavingColor(true);
      setMessage(null);

      const colorToSave = editedPrimaryColor.trim() || null;
      const result = await companyBrandingService.updatePrimaryColor(colorToSave, user.id);

      if (!result.success) {
        setMessage({ type: 'error', text: result.error || 'Erreur lors de la mise à jour.' });
        return;
      }

      await new Promise(resolve => setTimeout(resolve, 300));
      await refreshBranding();
      setMessage({ type: 'success', text: 'Couleur dominante mise à jour avec succès.' });
    } catch (error) {
      console.error('[ProfilePage] Save primary color error:', error);
      setMessage({ type: 'error', text: 'Erreur lors de la mise à jour de la couleur.' });
    } finally {
      setSavingColor(false);
    }
  };

  const handleLogoFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (file.size > 2 * 1024 * 1024) {
      setMessage({ type: 'error', text: 'Le fichier est trop volumineux. Taille maximale : 2 MB.' });
      return;
    }

    const allowedTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/svg+xml'];
    if (!allowedTypes.includes(file.type)) {
      setMessage({ type: 'error', text: 'Format non autorisé. Utilisez PNG, JPG, JPEG ou SVG.' });
      return;
    }

    setLogoFile(file);
    const reader = new FileReader();
    reader.onloadend = () => {
      setLogoPreview(reader.result as string);
    };
    reader.readAsDataURL(file);
  };

  const handleUploadLogo = async () => {
    if (!user?.id || !logoFile || !canCustomizePDF) return;

    try {
      setUploadingLogo(true);
      setMessage(null);

      console.log('[ProfilePage] Uploading logo...');
      const result = await companyBrandingService.uploadCompanyLogo(logoFile, user.id);

      if (!result.success) {
        console.error('[ProfilePage] Upload failed:', result.error);
        setMessage({ type: 'error', text: result.error || 'Erreur lors de l\'upload.' });
        return;
      }

      console.log('[ProfilePage] Upload successful, URL:', result.url);
      console.log('[ProfilePage] Waiting before refresh...');
      await new Promise(resolve => setTimeout(resolve, 500));

      console.log('[ProfilePage] Refreshing branding data...');
      await refreshBranding();

      setLogoFile(null);
      console.log('[ProfilePage] Logo upload complete');
      setMessage({ type: 'success', text: 'Logo uploadé avec succès.' });
    } catch (error) {
      console.error('[ProfilePage] Upload logo error:', error);
      setMessage({ type: 'error', text: 'Erreur lors de l\'upload du logo.' });
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleDeleteLogo = async () => {
    if (!user?.id || !canCustomizePDF) return;

    if (!confirm('Êtes-vous sûr de vouloir supprimer le logo de votre entreprise ?')) {
      return;
    }

    try {
      setDeletingLogo(true);
      setMessage(null);

      console.log('[ProfilePage] Deleting logo...');
      const result = await companyBrandingService.deleteCompanyLogo(user.id);

      if (!result.success) {
        console.error('[ProfilePage] Delete failed:', result.error);
        setMessage({ type: 'error', text: result.error || 'Erreur lors de la suppression.' });
        return;
      }

      console.log('[ProfilePage] Delete successful, waiting before refresh...');
      await new Promise(resolve => setTimeout(resolve, 500));

      console.log('[ProfilePage] Refreshing branding data...');
      await refreshBranding();

      setLogoPreview(null);
      setLogoFile(null);
      console.log('[ProfilePage] Logo deletion complete');
      setMessage({ type: 'success', text: 'Logo supprimé avec succès.' });
    } catch (error) {
      console.error('[ProfilePage] Delete logo error:', error);
      setMessage({ type: 'error', text: 'Erreur lors de la suppression du logo.' });
    } finally {
      setDeletingLogo(false);
    }
  };

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto">
        <div className="animate-pulse space-y-6">
          <div className="h-8 bg-gray-200 rounded w-1/3"></div>
          <div className="h-64 bg-gray-200 rounded"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto">
      <Helmet>
        <title>Mon profil - DPEWizard</title>
        <meta name="description" content="Gérez vos informations personnelles et les paramètres de votre entreprise." />
      </Helmet>

      <div className="mb-8">
        <Button
          variant="ghost"
          size="sm"
          className="mb-4"
          onClick={() => navigate('/dashboard')}
          icon={<ArrowLeft className="w-4 h-4 mr-2" />}
        >
          Retour au tableau de bord
        </Button>

        <h1 className="text-3xl font-bold text-gray-900 mb-2">
          Mon profil
        </h1>
        <p className="text-gray-600">
          Gérez vos informations personnelles et les paramètres de votre entreprise
        </p>
      </div>

      {message && (
        <Card className="mb-6">
          <CardContent className="p-4">
            <div className={`flex items-center gap-3 ${message.type === 'success' ? 'text-green-600' : 'text-red-600'}`}>
              {message.type === 'success' ? (
                <CheckCircle className="w-5 h-5 flex-shrink-0" />
              ) : (
                <AlertCircle className="w-5 h-5 flex-shrink-0" />
              )}
              <p>{message.text}</p>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="space-y-6">
        <Card>
          <CardHeader>
            <div className="flex items-center gap-3">
              <User className="w-6 h-6 text-blue-500" />
              <CardTitle>Informations personnelles</CardTitle>
            </div>
            <CardDescription>
              Ces informations sont visibles uniquement par vous
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Prénom
                </label>
                <Input
                  type="text"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  placeholder="Votre prénom"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Nom
                </label>
                <Input
                  type="text"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  placeholder="Votre nom"
                />
              </div>
            </div>
            <div className="flex justify-end">
              <Button
                onClick={handleSavePersonalInfo}
                loading={saving}
                disabled={saving}
                icon={<Save className="w-4 h-4 mr-2" />}
              >
                Enregistrer
              </Button>
            </div>
          </CardContent>
        </Card>

        {isProUser ? (
          <Card>
            <CardHeader>
              <div className="flex items-center gap-3">
                <Building2 className="w-6 h-6 text-blue-600" />
                <CardTitle className="flex items-center gap-2">
                  Paramètres entreprise
                  <Badge className="bg-gradient-to-r from-blue-600 to-purple-600 text-white">
                    <Crown className="w-3 h-3 mr-1" />
                    PRO
                  </Badge>
                </CardTitle>
              </div>
              <CardDescription>
                Personnalisez vos rapports PDF avec le nom et le logo de votre entreprise
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Nom de l'entreprise
                </label>
                <div className="flex gap-3">
                  <Input
                    type="text"
                    value={editedCompanyName}
                    onChange={(e) => setEditedCompanyName(e.target.value)}
                    placeholder="Nom de votre entreprise"
                    maxLength={100}
                  />
                  <Button
                    onClick={handleSaveCompanyName}
                    loading={saving}
                    disabled={saving || editedCompanyName === companyName}
                    icon={<Save className="w-4 h-4 mr-2" />}
                  >
                    Enregistrer
                  </Button>
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Ce nom apparaîtra sur tous vos rapports PDF
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Logo de l'entreprise
                </label>

                {logoPreview && (
                  <div className="mb-4 flex items-center gap-4">
                    <div className="border border-gray-300 rounded-lg p-4 bg-white">
                      <img
                        src={logoPreview}
                        alt="Logo entreprise"
                        className="max-w-[200px] max-h-[100px] object-contain"
                      />
                    </div>
                    <Button
                      onClick={handleDeleteLogo}
                      loading={deletingLogo}
                      disabled={deletingLogo}
                      variant="outline"
                      icon={<X className="w-4 h-4 mr-2" />}
                    >
                      Supprimer
                    </Button>
                  </div>
                )}

                <div className="flex items-center gap-3">
                  <label className="cursor-pointer">
                    <input
                      type="file"
                      accept="image/png,image/jpeg,image/jpg,image/svg+xml"
                      onChange={handleLogoFileChange}
                      className="hidden"
                    />
                    <div className="inline-flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors">
                      <Upload className="w-4 h-4" />
                      <span className="text-sm font-medium">
                        {logoFile ? 'Changer le fichier' : 'Choisir un fichier'}
                      </span>
                    </div>
                  </label>

                  {logoFile && (
                    <Button
                      onClick={handleUploadLogo}
                      loading={uploadingLogo}
                      disabled={uploadingLogo}
                      icon={<Upload className="w-4 h-4 mr-2" />}
                    >
                      Uploader
                    </Button>
                  )}
                </div>

                <p className="text-xs text-gray-500 mt-2">
                  Formats acceptés : PNG, JPG, JPEG, SVG - Taille max : 2 MB
                </p>
                {logoFile && (
                  <p className="text-sm text-blue-600 mt-1">
                    Fichier sélectionné : {logoFile.name}
                  </p>
                )}
              </div>

              {/* Couleur dominante */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                  <Palette className="w-4 h-4 text-blue-500" />
                  Couleur dominante des rapports PDF
                </label>

                {/* Swatches prédéfinis */}
                <div className="flex flex-wrap gap-2 mb-3">
                  {[
                    { label: 'Bleu', value: '#2563EB' },
                    { label: 'Indigo', value: '#4F46E5' },
                    { label: 'Violet', value: '#7C3AED' },
                    { label: 'Rose', value: '#DB2777' },
                    { label: 'Rouge', value: '#DC2626' },
                    { label: 'Orange', value: '#EA580C' },
                    { label: 'Vert', value: '#16A34A' },
                    { label: 'Teal', value: '#0D9488' },
                    { label: 'Gris', value: '#374151' },
                    { label: 'Noir', value: '#111827' },
                  ].map(({ label, value }) => (
                    <button
                      key={value}
                      type="button"
                      title={label}
                      onClick={() => setEditedPrimaryColor(value)}
                      className={`w-7 h-7 rounded-full border-2 transition-transform hover:scale-110 ${
                        editedPrimaryColor === value
                          ? 'border-gray-800 scale-110 ring-2 ring-offset-1 ring-gray-400'
                          : 'border-white shadow'
                      }`}
                      style={{ backgroundColor: value }}
                    />
                  ))}
                  {/* Remise à zéro */}
                  {editedPrimaryColor && (
                    <button
                      type="button"
                      title="Réinitialiser (bleu DPEWizard)"
                      onClick={() => setEditedPrimaryColor('')}
                      className="w-7 h-7 rounded-full border-2 border-dashed border-gray-300 flex items-center justify-center text-gray-400 hover:border-gray-500 hover:text-gray-500 transition-colors"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  )}
                </div>

                {/* Picker natif + hex */}
                <div className="flex items-center gap-3">
                  <input
                    type="color"
                    value={editedPrimaryColor || '#2563EB'}
                    onChange={(e) => setEditedPrimaryColor(e.target.value)}
                    className="w-10 h-10 rounded cursor-pointer border border-gray-300 p-0.5 bg-white"
                    title="Choisir une couleur personnalisée"
                  />
                  <input
                    type="text"
                    value={editedPrimaryColor}
                    onChange={(e) => setEditedPrimaryColor(e.target.value)}
                    placeholder="#2563EB"
                    maxLength={7}
                    className="w-28 px-3 py-2 text-sm border border-gray-300 rounded-md font-mono focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                  {editedPrimaryColor && (
                    <div
                      className="h-8 w-24 rounded border border-gray-200 flex items-center justify-center text-xs font-medium"
                      style={{
                        backgroundColor: editedPrimaryColor,
                        color: '#fff',
                        textShadow: '0 1px 2px rgba(0,0,0,0.4)'
                      }}
                    >
                      Aperçu
                    </div>
                  )}
                  <Button
                    onClick={handleSavePrimaryColor}
                    loading={savingColor}
                    disabled={savingColor || editedPrimaryColor === (primaryColor ?? '')}
                    icon={<Save className="w-4 h-4 mr-2" />}
                    size="sm"
                  >
                    Enregistrer
                  </Button>
                </div>

                <p className="text-xs text-gray-500 mt-2">
                  Cette couleur sera utilisée pour les en-têtes, accents et badges de vos rapports PDF.
                  Laissez vide pour le bleu DPEWizard par défaut.
                </p>
              </div>
            </CardContent>
          </Card>
        ) : (
          <Card className="border-2 border-blue-200 bg-blue-50">
            <CardHeader>
              <div className="flex items-center gap-3">
                <Crown className="w-6 h-6 text-blue-600" />
                <CardTitle>Passez à Unlimited Power Pro</CardTitle>
              </div>
              <CardDescription>
                Personnalisez vos rapports PDF avec votre logo et nom d'entreprise
              </CardDescription>
            </CardHeader>
            <CardContent>
              <p className="text-gray-700 mb-4">
                Avec l'abonnement Pro, vous pouvez personnaliser tous vos rapports PDF avec votre identité visuelle professionnelle.
              </p>
              <Button
                onClick={() => navigate('/pricing')}
                className="bg-gradient-to-r from-blue-600 to-purple-600 text-white hover:from-blue-700 hover:to-purple-700"
                icon={<Crown className="w-4 h-4 mr-2" />}
              >
                Découvrir l'offre Pro
              </Button>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default ProfilePage;
